import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { connect, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import validator from "validator";
import { X } from "lucide-react";
import { fetchCompanyDetails } from "../../api/action/company";
import { googleSignin, register } from "../../api/action/user";
import { getDefaultCountryCode, getPhonePlaceholder } from "../../config/locationConfig.js";
import { InputTextField } from "../../components/InuptTextField.jsx";
import StyledTooltip from "../../components/StyledTooltip.jsx";
import User from "../../model/User";
import { GOOGLE_SIGNIN_CANCELLED, isNativeMobileApp, signInWithGoogleNative } from "../../utils/googleAuth.js";
import GoogleAuthButton from "../../components/GoogleAuthButton.jsx";
import { useAuthSession } from "../../auth/useAuthSession.js";
import "../../styles/retailAuth.css";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || "";

const toastClasses = {
  error: "border-red-200 bg-red-600 text-white",
  success: "border-emerald-200 bg-emerald-600 text-white",
  info: "border-blue-200 bg-blue-600 text-white",
  warning: "border-amber-200 bg-amber-500 text-white",
};

const ToastMessage = ({ open, severity = "success", message, onClose }) => {
  if (!open || !message) return null;

  const toast = (
    <div className="academy-signup-toast fixed left-1/2 top-6 z-[60] w-[calc(100%-2rem)] max-w-md -translate-x-1/2">
      <div
        role={severity === "error" ? "alert" : "status"}
        className={`flex w-full min-w-0 items-start gap-3 rounded-lg border px-4 py-3 text-sm shadow-xl ${toastClasses[severity] || toastClasses.info}`}
      >
        <span className="min-w-0 flex-1 whitespace-normal break-words leading-relaxed">{message}</span>
        <button
          type="button"
          aria-label="Close message"
          onClick={onClose}
          className="rounded p-0.5 text-white/80 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-white/70"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );

  return typeof document === "undefined" ? toast : createPortal(toast, document.body);
};

const Signup = (props) => {
  const [user, setUser] = useState(new User());
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [flag, setFlag] = useState(false);
  const companyState = useSelector((state) => state.companyReducer.company);
  const countryCode = getDefaultCountryCode(companyState);
  const phonePlaceholder = getPhonePlaceholder(companyState);
  const [isDisabled] = useState(false);
  const [, /*allValid*/ setAllValid] = useState(false);
  const [firstNameHelpError, setFirstNameHelpError] = useState("");
  const [lastNameHelpError, setLastNameHelpError] = useState("");
  const [phoneNumberHelpError, setPhoneNumberHelpError] = useState(false);
  const [passHelpError, setPassHelpError] = useState(false);
  const [confirmPassHelpError, setConfirmPassHelpError] = useState(false);
  const [, setCompany] = useState(null);
  const [, setSubdomain] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isButtonClicked] = useState(false);
  const [passwordRules, setPasswordRules] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const navigate = useNavigate();
  const { isAuthenticated, isLoggedIn } = useAuthSession();
  const [phoneTakenError, setPhoneTakenError] = useState(false);
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const handleGoogleCredentialResponseRef = useRef(null);
  const isRetailDomain = String(
    props.templateDomainType || localStorage.getItem("domainType") || ""
  ).toUpperCase() === "RETAIL";

  // Add terms and conditions state
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [userIP, setUserIP] = useState("");

  // Fetch user IP address on component mount
  useEffect(() => {
    const fetchUserIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setUserIP(data.ip);
      } catch {
        // Fallback to a default or try another service
        try {
          const fallbackResponse = await fetch('https://ipapi.co/json/');
          const fallbackData = await fallbackResponse.json();
          setUserIP(fallbackData.ip);
        } catch {
          setUserIP("unknown");
        }
      }
    };

    fetchUserIP();
  }, []);

  const handleClickShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleClickConfirmPassword = useCallback(() => {
    setConfirmPassword((prev) => !prev);
  }, []);

  const createLegalRequirement = () => {
    const acceptedDate = Date.now();

    const baseDetails = {
      type: "TERMS_AND_CONDITIONS",
      acceptedDate: acceptedDate,
      accepted: true,
      ipAddress: userIP
    };

    const legalRequirement = {
      ...baseDetails,
      history: [
        { ...baseDetails }
      ]
    };

    return legalRequirement;
  };

  // Create PersonalPreference object with LegalRequirements
  const createPersonalPreference = () => {
    const legalRequirement = createLegalRequirement();

    const personalPreference = {
      communicationPreferences: [], // Add communication preferences if needed
      legalRequirements: [legalRequirement]
    };

    return personalPreference;
  };

  const handleTermsClick = (e) => {
    e.preventDefault();

    const legalDocuments = companyState?.legalDocuments;

    if (legalDocuments && legalDocuments.length > 0) {
      const termsDoc = legalDocuments.find(doc =>
        doc.contentSubType?.toLowerCase() === 'terms_and_conditions' ||
        doc.name?.toLowerCase().includes('terms') ||
        doc.contentSubType?.toLowerCase().includes('terms')
      );

      if (termsDoc) {
        const policyName = termsDoc.contentSubType?.toLowerCase() || 'terms-and-conditions';
        navigate(`/policy/${policyName}`);
      } else {
        navigate('/policy/terms-and-conditions');
      }
    } else {
      navigate('/policy/terms-and-conditions');
    }
  };

  const handleTermsChange = (event) => {
    const isAccepted = event.target.checked;
    setAcceptedTerms(isAccepted);
    setTermsError(false);

    // Update user object with personalPreference if accepted
    if (isAccepted) {
      const personalPreference = createPersonalPreference();
      setUser(prevUser => ({
        ...prevUser,
        personalPreference: personalPreference
      }));
    } else {
      // Remove personalPreference if unchecked
      setUser(prevUser => {
        const { personalPreference, ...rest } = prevUser;
        return rest;
      });
    }
  };

  useEffect(() => {
    if (!user.password) return;
    const rules = [
      {
        text: "At least 8 characters",
        valid: user.password.length >= 8,
      },
      {
        text: "Spaces not allowed",
        valid: !/\s/.test(user.password),
      },
      {
        text: "1 uppercase letter (A-Z)",
        valid: /[A-Z]/.test(user.password),
      },
      {
        text: "1 lowercase letter (a-z)",
        valid: /[a-z]/.test(user.password),
      },
      {
        text: "1 number (0-9)",
        valid: /[0-9]/.test(user.password),
      },
      {
        text: "1 special character (!@#$)",
        valid: /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(user.password),
      },
    ];

    const formattedRules = rules
      .map((rule) => `${rule.valid ? " " : "x"} ${rule.text}`)
      .join("\n");

    setPasswordRules(`𝗣𝗮𝘀𝘀𝘄𝗼𝗿𝗱 𝗺𝘂𝘀𝘁 𝗰𝗼𝗻𝘁𝗮𝗶𝗻:\n${formattedRules}`);
  }, [user]);

  useEffect(() => {
    const fullUrl = window.location.href;
    const currentSubdomain = fullUrl.split("/").slice(0, 3).join("/");
    setSubdomain(currentSubdomain);
    fetchCompanyDetails(currentSubdomain)
      .then((response) => {
        setCompany(response.data);
      })
      .catch(() => {
        setCompany(null);
      });
  }, []);

  function getNameError(name) {
    const letters = /^[A-Za-z]*$/;
    if (name.length < 1) {
      return "Enter at least 1 character";
    }
    if (!letters.test(name)) {
      return "Alphabetical characters only";
    }
    return "";
  }

  function confirmPasswordsMatch(password, confirmPassword) {
    if (
      typeof password !== "string" ||
      typeof confirmPassword !== "string" ||
      password.length < 1 ||
      confirmPassword.length < 1
    ) {
      return false;
    }
    return password === confirmPassword;
  }

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";
    return phoneNumber.replace(/\D/g, "");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedValue =
      name === "phoneNumber" ? formatPhoneNumber(value).slice(0, 10) : value;

    setUser((prevUser) => ({ ...prevUser, [name]: updatedValue }));

    switch (name) {
      case "firstName":
        if (firstNameHelpError) {
          setFirstNameHelpError(getNameError(value));
        }
        break;

      case "lastName":
        if (lastNameHelpError) {
          setLastNameHelpError(getNameError(value));
        }
        break;

      // case "email":
      //   setEmailTakenError(false);
      //   setEmailHelpError(!validator.isEmail(value));
      //   break;
      case "phoneNumber":
        setPhoneTakenError(false);
        setPhoneNumberHelpError(false);
        break;
      case "password":
        setPassHelpError(!validator.isStrongPassword(value));
        break;

      case "confirmPassword":
        if (user.password) {
          setConfirmPassHelpError(!confirmPasswordsMatch(user.password, value));
        }
        break;

      default:
        break;
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let updates = {};

    if (name === "firstName") {
      updates.firstNameHelpError = getNameError(value);
    } else if (name === "lastName") {
      updates.lastNameHelpError = getNameError(value);
    }
    // else if (name === "email") {
    //   updates.emailHelpError = !validator.isEmail(value);
    // }

    setUser((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const checkAllValid = () => {
    if (
      firstNameHelpError === "" &&
      lastNameHelpError === "" &&
      // emailHelpError === false &&
      phoneNumberHelpError === false &&
      passHelpError === false &&
      confirmPassHelpError === false &&
      acceptedTerms
    ) {
      setAllValid(true);
      return true;
    } else {
      setAllValid(false);
      return false;
    }
  };

  const clearFields = () => {
    const domainType = props.templateDomainType || localStorage.getItem("domainType");
    const initialUserRole =
      domainType === "RETAIL" || domainType === "FLEX_OFFICE" ? "CUSTOMER" : "STUDENT";
    setUser({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      countryCode: "",
      userRoles: [initialUserRole],
      password: "",
      confirmPassword: "",
      // Don't clear personalPreference here as it's managed separately
    });
    setAcceptedTerms(false); // Reset terms checkbox
    // setEmailTakenError(false);
    setPhoneTakenError(false);
  };

  const [, setEmailTakenError] = useState(false);

  const getDuplicateField = (response) => {
    const combinedText = `${response?.message || ""} ${response?.status || ""}`.toLowerCase();

    if (combinedText.includes("email") && (combinedText.includes("already") || combinedText.includes("exists") || combinedText.includes("registered") || combinedText.includes("taken"))) {
      return "email";
    }

    if ((combinedText.includes("phone") || combinedText.includes("mobile")) && (combinedText.includes("already") || combinedText.includes("exists") || combinedText.includes("registered") || combinedText.includes("taken"))) {
      return "phone";
    }

    return "";
  };

  const showDuplicateMessage = (duplicateField, roleAdded = false, userRole = "STUDENT") => {
    setEmailTakenError(duplicateField === "email");
    setPhoneTakenError(duplicateField === "phone");

    const baseMessage = duplicateField === "phone"
      ? "This phone number already exists."
      : "This email already exists.";

    const message = roleAdded
      ? `${baseMessage} The ${userRole} role has been added to your existing account. Please sign in using your current credentials.`
      : `${baseMessage} Please sign in instead.`;

    setSnackbarMessage(message);
    setSnackbarSeverity(roleAdded ? "info" : "error");
    setSnackbarOpen(true);
  };

  const handleSuccessfulGoogleLogin = async (response, emailOverride = null) => {
    const responseData = response?.data || response?.payload?.data || response;
    const userRoles = responseData?.userRoles || [];
    const token = responseData?.accessConfirmationToken ||
      responseData?.token ||
      response?.token ||
      response?.payload?.token;
    const userEmail = emailOverride || responseData?.email;

    try {
      if (token) localStorage.setItem("token", token);
      if (userEmail) localStorage.setItem("email", userEmail);
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("userRoles", JSON.stringify(userRoles));
      if (responseData) {
        localStorage.setItem("userData", JSON.stringify(responseData));
      }
    } catch (storageError) {
      setSnackbarMessage(storageError?.message || "Unable to store login details. Please sign in again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setGoogleLoading(false);
      return;
    }

    const primaryRole = userRoles[0] || "STUDENT";

    if (primaryRole === "STUDENT") {
      setGoogleLoading(false);
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 100);
      return;
    }

    setGoogleLoading(false);
    setSnackbarMessage("Login successful! Redirecting...");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setTimeout(() => navigate("/"), 1000);
  };

  const handleGoogleRegistration = async (email, firstName, lastName, idToken) => {
    try {
      const requestData = {
        firstName: firstName || "Google",
        lastName: lastName || "User",
        email: email,
        authProvider: "GOOGLE",
        authToken: idToken,
        company: companyState,
        userRoles: ["STUDENT"],
        createdBy: "GOOGLE_OAUTH",
        personalPreference: createPersonalPreference()
      };

      const res = await props.register(requestData);
      const duplicateField = getDuplicateField(res);
      const responseData = res?.data || res;
      const roles = responseData?.userRoles || [];
      const isExistingUserWithNewRole = roles.length > 1;

      if (duplicateField && !isExistingUserWithNewRole) {
        showDuplicateMessage(duplicateField, false, "STUDENT");
        setGoogleLoading(false);
        return;
      }

      if (isExistingUserWithNewRole && duplicateField) {
        showDuplicateMessage(duplicateField, true, "STUDENT");
      }

      if (res?.status === "NEW" || res?.data?.status === "NEW" || res?.payload?.status === "NEW" || isExistingUserWithNewRole) {
        setSnackbarMessage("Registration successful! Logging you in...");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);

        const signinResponse = await props.googleSignin({
          email,
          authProvider: "GOOGLE",
          userRoles: ["STUDENT"],
          authToken: idToken,
          company: { primaryId: companyState?.primaryId },
        });

        await handleSuccessfulGoogleLogin(signinResponse, email);
      } else {
        setSnackbarMessage(res?.message || "Registration failed. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setGoogleLoading(false);
      }
    } catch (err) {
      setSnackbarMessage(err.message || "Google registration failed");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setGoogleLoading(false);
    }
  };

  const handleGoogleCredentialResponse = async (response) => {
    setGoogleLoading(true);
    setError("");
    setEmailTakenError(false);
    setPhoneTakenError(false);

    const idToken = response?.credential;

    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const googleEmail = payload?.email;
      const givenName = payload?.given_name || "";
      const familyName = payload?.family_name || "";

      if (!googleEmail) {
        throw new Error("Email not received from Google");
      }

      const signinPayload = {
        email: googleEmail,
        authProvider: "GOOGLE",
        userRoles: ["STUDENT"],
        authToken: idToken,
        company: { primaryId: companyState?.primaryId },
      };

      try {
        const resultAction = await props.googleSignin(signinPayload);

        const isSuccess =
          resultAction?.type?.includes('SUCCESS') ||
          resultAction?.payload?.status === 200 ||
          resultAction?.status === 200 ||
          resultAction?.data?.status === 200 ||
          resultAction?.payload?.message?.toLowerCase().includes("success") ||
          resultAction?.payload?.message?.toLowerCase().includes("login successful") ||
          resultAction?.message?.toLowerCase().includes("success");

        if (isSuccess) {
          const responseData = resultAction?.payload?.data ||
            resultAction?.data ||
            resultAction?.payload ||
            resultAction;

          await handleSuccessfulGoogleLogin(responseData, googleEmail);
          return;
        }
      } catch {
        // Continue with registration when the Google account is not already linked.
      }

      await handleGoogleRegistration(googleEmail, givenName, familyName, idToken);
    } catch (err) {
      setSnackbarMessage(err?.message || "Google sign in error. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setGoogleLoading(false);
    }
  };
  handleGoogleCredentialResponseRef.current = handleGoogleCredentialResponse;

  const handleNativeGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError("");
    try {
      const idToken = await signInWithGoogleNative();
      await handleGoogleCredentialResponseRef.current?.({ credential: idToken });
    } catch (err) {
      if (err?.message !== GOOGLE_SIGNIN_CANCELLED) {
        setSnackbarMessage(err?.message || "Google sign in error. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
      setGoogleLoading(false);
    }
  };

  useEffect(() => {
    if (isNativeMobileApp()) {
      return undefined;
    }

    const initializeGoogleOneTap = () => {
      if (!window.google || !window.google.accounts) {
        return;
      }

      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => handleGoogleCredentialResponseRef.current?.(response),
          auto_select: false,
          cancel_on_tap_outside: true,
          context: "signup"
        });

        const googleButtonElement = document.getElementById("googleSignInButton");
        if (googleButtonElement) {
          const measuredWidth = googleButtonElement.getBoundingClientRect().width || googleButtonElement.offsetWidth;
          const viewportFallback = typeof window !== "undefined" ? window.innerWidth - 48 : 360;
          const buttonWidth = Math.min(430, Math.max(280, Math.floor(measuredWidth || viewportFallback || 360)));

          googleButtonElement.innerHTML = "";
          window.google.accounts.id.renderButton(
            googleButtonElement,
            {
              type: "standard",
              theme: "outline",
              size: "large",
              text: "signup_with",
              shape: "rectangular",
              logo_alignment: "left",
              width: buttonWidth
            }
          );
        }

        setTimeout(() => {
          if (!(isAuthenticated || isLoggedIn)) {
            window.google.accounts.id.prompt();
          }
        }, 1000);
      } catch {
        setGoogleScriptLoaded(false);
      }
    };

    if (window.google) {
      setGoogleScriptLoaded(true);
      initializeGoogleOneTap();
      return undefined;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setGoogleScriptLoaded(true);
      initializeGoogleOneTap();
    };
    script.onerror = () => {
      setGoogleScriptLoaded(false);
    };
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [isAuthenticated, isLoggedIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate terms and conditions
    if (!acceptedTerms) {
      setTermsError(true);
      setSnackbarMessage("You must accept the terms and conditions to continue.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // Ensure personalPreference is set
    if (!user.personalPreference) {
      const personalPreference = createPersonalPreference();
      setUser(prevUser => ({
        ...prevUser,
        personalPreference: personalPreference
      }));
    }

    const domainType = props.templateDomainType || localStorage.getItem("domainType");
    const userRole = domainType === "RETAIL" || domainType === "FLEX_OFFICE" ? "CUSTOMER" : "STUDENT";

    if (isSubmitting || !checkAllValid()) return;

    setIsSubmitting(true);
    setIsLoading(true);
    setEmailTakenError(false);
    setPhoneTakenError(false);
    setFlag(false);

    try {
      if (!confirmPasswordsMatch(user.password, user.confirmPassword)) {
        setConfirmPassHelpError(true);
        setSnackbarMessage("Passwords do not match.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setIsLoading(false);
        return;
      }

      const companyDetails = companyState;

      // Prepare request data with personalPreference
      const requestData = {
        ...user,
        countryCode,
        company: companyDetails,
        userRoles: [userRole],
        createdBy: "ADMIN",
        // personalPreference is already included in user object
      };

      // Call register API
      const res = await props.register(requestData);

      // CRITICAL: Check the actual response message from backend
      // Your backend returns error messages even with 200 status

      const duplicateField = getDuplicateField(res);

      if (duplicateField && !res?.data) {
        showDuplicateMessage(duplicateField, false, userRole);
        return;
      }

      if (res.message) {
        // Check for other registration failures
        if (res.message.includes("Registration failed")) {
          setSnackbarMessage(res.message);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
          return;
        }
      }

      // Check if it's a success response
      if (res.status === "Success" || res.code === 200 || res.data) {
        if (res?.data || res?.primaryId) {
          const responseData = res.data || res;

          const roles = responseData.userRoles || [];

          const isExistingUserWithNewRole = roles.length > 1;
          const isNewUser = roles.length === 1;

          if (isExistingUserWithNewRole) {
            if (duplicateField) {
              showDuplicateMessage(duplicateField, true, userRole);
            } else {
              setSnackbarMessage(
                `You already have an account. The new role (${userRole}) has been added. You can sign in using your existing credentials.`
              );
              setSnackbarSeverity("info");
            }
            setFlag(false); // ❌ no activation mail
          }
          else if (isNewUser) {
            setSnackbarMessage(
              "Registration successful! Activation link has been sent to your email."
            );
            setSnackbarSeverity("success");
            setFlag(true); //  activation message
          }

          clearFields();
          setSnackbarOpen(true);
          return;
        }


        // Generic success
        setSnackbarMessage("Registration successful!");
        setSnackbarSeverity("success");
        clearFields();
        setFlag(true);
        setSnackbarOpen(true);
      }

    } catch (err) {
      // Handle network errors
      let errorMessage = err.message || "Registration failed";

      if (errorMessage.includes("Failed to fetch")) {
        errorMessage = "Cannot connect to server. Please check your connection.";
      }

      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);

    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated || isLoggedIn) {
      navigate("/products");
    }
  }, [isAuthenticated, isLoggedIn, navigate]);

  const signin = (e) => {
    e.preventDefault();
    navigate("/sign-in");
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (!snackbarOpen) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setSnackbarOpen(false);
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [snackbarOpen]);

  return (
    <div
      className={`academy-signup-page flex min-h-screen flex-col items-center justify-center px-4 pb-10 pt-24 font-[sans-serif] sm:px-6 lg:px-12 ${isRetailDomain ? "retail-auth-theme" : ""}`}
      style={{ backgroundColor: isRetailDomain ? '#f4a649' : 'var(--education-primary, #746EAC)' }}
    >
      {isLoading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
            <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mb-4"></div>
            <p className="text-gray-800">Creating your account...</p>
          </div>
        </div>
      )}
      <div className="academy-signup-card grid w-full max-w-5xl items-start overflow-hidden rounded-xl shadow-[0_18px_50px_-24px_rgba(17,24,39,0.35)] md:grid-cols-2">
        <div className="academy-signup-info hidden md:flex h-full w-full flex-col justify-center p-6 sm:p-8" style={{ backgroundColor: "var(--education-primary-dark, rgb(94 88 151))" }}>
          <div className="max-w-md space-y-12 mx-auto">
            <div>
              <h4 className="text-white text-lg font-semibold">Create Your Account</h4>
              <p className="text-[13px] text-gray-200 mt-2">Welcome to our registration page! Get started by creating your account.</p>
            </div>
            <div>
              <h4 className="text-white text-lg font-semibold">Simple & Secure Registration</h4>
              <p className="text-[13px] text-gray-200 mt-2">Our registration process is designed to be straightforward and secure. We prioritize your privacy and data security.</p>
            </div>
            <div>
              <h4 className="text-white text-lg font-semibold">Terms and Conditions Agreement</h4>
              <p className="text-[13px] text-gray-200 mt-2">By registering, you agree to our Terms and Conditions. Your acceptance will be recorded with your IP address and timestamp.</p>
            </div>
          </div>
        </div>

        <form className="academy-signup-form w-full bg-white p-5 sm:p-8" onSubmit={handleSubmit}>
          <div className="mb-7">
            <h3 className="text-gray-800 text-3xl font-bold text-center">Register</h3>
          </div>

          {flag ? (
            <p className="bg-purple-100 border-l-4 border-[#9c27b0] text-[#9c27b0] font-bold text-sm text-center mb-4 p-4 rounded">
              The activation link has been sent to the email,
              <br />
              please activate it within 15 minutes.
            </p>
          ) : (
            error && <p className="text-red-500 text-center mb-4">{error}</p>
          )}

          <div className="academy-signup-fields grid grid-cols-1 gap-5 xl:grid-cols-2">
            {/* First Name */}
            <div className="academy-signup-field">
              <label className="text-gray-800 text-sm mb-2 block">First Name</label>
              <InputTextField
                id="Register-firstname-reddittextfield"
                name="firstName"
                value={user.firstName}
                onBlur={handleBlur}
                onChange={handleChange}
                required
                helperText={<span className="text-red-500">{firstNameHelpError}</span>}
              />
            </div>

            {/* Last Name */}
            <div className="academy-signup-field">
              <label className="text-gray-800 text-sm mb-2 block">Last Name</label>
              <InputTextField
                id="Register-lastname-reddittextfield"
                name="lastName"
                value={user.lastName}
                onBlur={handleBlur}
                onChange={handleChange}
                required
                helperText={<span className="text-red-500">{lastNameHelpError}</span>}
              />
            </div>

            {/* Email */}
            <div className="academy-signup-field">
              <label className="text-gray-800 text-sm mb-2 block">Email</label>
              <InputTextField
                id="Register-email-reddittextfield"
                name="email"
                value={user.email}
                onBlur={handleBlur}
                onChange={handleChange}
              // error={emailHelpError || emailTakenError}
              // helperText={
              //   emailHelpError ? (
              //     <span style={{ textTransform: "none" }} className="text-red-500">
              //       Please enter a valid email.
              //     </span>
              //   ) : emailTakenError ? (
              //     <span style={{ textTransform: "none" }} className="text-red-500">
              //       Email already taken. Please use a different email.
              //     </span>
              //   ) : (
              //     ""
              //   )
              // }
              />
            </div>

            {/* Phone */}
            <div className="academy-signup-field">
              <label className="text-gray-800 text-sm mb-2 block">Phone</label>
              <InputTextField
                id="Register-phoneNumber-reddittextfield"
                name="phoneNumber"
                autoComplete="off"
                onBlur={handleBlur}
                onChange={handleChange}
                value={user.phoneNumber || ""}
                placeholder={phonePlaceholder}
                required
                error={phoneNumberHelpError || phoneTakenError}
                InputProps={{
                  startAdornment: <span className="text-gray-500">{countryCode}</span>,
                }}
                helperText={
                  phoneNumberHelpError ? (
                    <span style={{ textTransform: "none" }} className="text-red-500">
                      Please enter a valid phone number.
                    </span>
                  ) : phoneTakenError ? (
                    <span style={{ textTransform: "none" }} className="text-red-500">
                      Phone number already taken. Please use a different phone number.
                    </span>
                  ) : null
                }
              />
            </div>

            {/* Password */}
            <div className="academy-signup-field">
              <StyledTooltip title={passwordRules} placement="left" arrow className="block w-full min-w-0">
                <div>
                  <InputTextField
                    label="Password"
                    id="Register-password-reddittextfield"
                    name="password"
                    type="password"
                    value={user.password}
                    onChange={handleChange}
                    showPassword={showPassword}
                    onTogglePassword={handleClickShowPassword}
                    error={passHelpError}
                    helperText={
                      passHelpError ? (
                        <span className="text-red-500 text-xs mt-1">
                          Please enter a strong password
                        </span>
                      ) : (
                        ""
                      )
                    }
                    fullWidth
                    required
                  />
                </div>
              </StyledTooltip>
            </div>

            {/* Confirm Password */}
            <div className="academy-signup-field">
              <InputTextField
                label="Confirm Password"
                id="Register-confirmPassword-reddittextfield"
                name="confirmPassword"
                type="password"
                value={user.confirmPassword}
                onChange={handleChange}
                showPassword={confirmPassword}
                onTogglePassword={handleClickConfirmPassword}
                error={confirmPassHelpError}
                helperText={
                  confirmPassHelpError ? (
                    <span className="text-red-500 text-xs mt-1">
                      Passwords do not match
                    </span>
                  ) : (
                    ""
                  )
                }
                fullWidth
                required
              />
            </div>

            {/* Terms and Conditions Checkbox */}
            <div className="academy-signup-terms xl:col-span-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={handleTermsChange}
                  id="Register-terms-checkbox"
                  className={`h-4 w-4 shrink-0 rounded border-gray-300 focus:ring-2 focus:ring-offset-1 ${
                    termsError
                      ? "text-red-600 focus:ring-red-500"
                      : "education-primary-text focus:ring-[var(--education-primary,#746EAC)]"
                  }`}
                />
                <span className="min-w-0 inline-flex flex-wrap items-center gap-x-1 gap-y-0.5">
                  <span>I agree to the</span>
                  <button
                    type="button"
                    onClick={handleTermsClick}
                    className="education-primary-text inline whitespace-nowrap cursor-pointer border-none bg-transparent p-0 hover:underline focus:outline-none focus:ring-2 focus:ring-[var(--education-primary,#746EAC)]"
                  >
                    Terms and Conditions
                  </button>
                </span>
              </label>
              {termsError && (
                <p className="text-red-500 text-xs mt-1 ml-7">
                  You must accept the terms and conditions
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="academy-signup-submit xl:col-span-2">
              <button
                id="Register-signup-button"
                className={`w-full education-primary-button text-white font-semibold py-3 rounded-lg transition duration-200 flex items-center justify-center space-x-2 ${isDisabled || isButtonClicked || !acceptedTerms ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                style={{ backgroundColor: "var(--education-primary, #746EAC)" }}
                type="submit"
                disabled={isDisabled || isButtonClicked || !acceptedTerms}
              >
                <span>Sign Up</span>
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="mt-6 text-center">
        <p className="text-white">
          Already have an account?{" "}
          <a href="/sign-in" onClick={signin} id="Register-signin-link" className="text-[#d1c4e9] hover:underline">
            Sign In
          </a>
        </p>
        <p className="text-white mt-2">or</p>
        <div className="flex justify-center mt-4">
          {isNativeMobileApp() ? (
            <div className="w-full max-w-sm">
              <GoogleAuthButton onClick={handleNativeGoogleSignIn} disabled={googleLoading} />
            </div>
          ) : (
            <div id="googleSignInButton" className="w-full max-w-sm">
              {googleLoading && googleScriptLoaded && (
                <div className="flex items-center justify-center py-2">
                  <span className="text-sm text-white font-medium">Signing in with Google...</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ToastMessage
        open={snackbarOpen}
        severity={snackbarSeverity}
        message={snackbarMessage}
        onClose={handleCloseSnackbar}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  // isAuthenticated: state?.auth?.isAuthenticated,
  // isLoggedIn: state?.auth?.data.isLoggedIn,
  // user: state?.auth?.data,
});

export default connect(mapStateToProps, { register, googleSignin })(Signup);
